import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/layout/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", open_time: "10:00", close_time: "12:00", status: "active", days_active: ["mon","tue","wed","thu","fri","sat"] };

export default function AdminMarkets() {
  const [markets, setMarkets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/markets").then(({data})=>setMarkets(data));
  useEffect(() => { load(); }, []);

  const save = async (m) => {
    try {
      if (m.id) {
        await api.patch(`/admin/markets/${m.id}`, m);
        toast.success("Market updated");
      } else {
        await api.post("/admin/markets", m);
        toast.success("Market created");
      }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this market?")) return;
    try { await api.delete(`/admin/markets/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cleanupDefaults = async () => {
    if (!window.confirm("Saare purane markets (default list mein nahi hain) delete kar denge. Sahi hai?")) return;
    try {
      const { data } = await api.post("/admin/markets/cleanup-defaults", {});
      toast.success(`${data.deleted} purane markets delete kar diye`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Management</div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-slate-900 mt-1">Markets</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="cleanup-defaults-btn" onClick={cleanupDefaults} variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50">
            <Trash2 className="w-4 h-4 mr-1" /> Cleanup Old Markets
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-market-btn" onClick={()=>setEditing({...empty})} className="btn-brand"><Plus className="w-4 h-4 mr-1" /> Add Market</Button>
            </DialogTrigger>
            <MarketDialog editing={editing} setEditing={setEditing} save={save} />
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="markets-table">
        {/* Desktop / tablet: table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Open</th>
                <th className="text-left px-4 py-3">Close</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Result</th>
                <th className="text-left px-4 py-3">Reminder</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {markets.map(m => (
                <tr key={m.id} className="border-t border-slate-100" data-testid={`row-${m.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{m.name}</td>
                  <td className="px-4 py-3 tabular-nums">{m.open_time}</td>
                  <td className="px-4 py-3 tabular-nums">{m.close_time}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-md ${m.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{m.status}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{m.open_result || "***"} - {m.close_result || "***"}</td>
                  <td className="px-4 py-3 text-xs">
                    {m.reminder_message ? <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-1 rounded-md">🔔 Set</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <Dialog open={open && editing?.id === m.id} onOpenChange={(o)=>{ setOpen(o); if(!o) setEditing(null); }}>
                        <DialogTrigger asChild>
                          <button data-testid={`edit-${m.id}`} onClick={()=>{setEditing({...m}); setOpen(true);}} className="p-1.5 hover:bg-slate-100 rounded-md"><Pencil className="w-4 h-4 text-slate-600" /></button>
                        </DialogTrigger>
                        <MarketDialog editing={editing} setEditing={setEditing} save={save} />
                      </Dialog>
                      <button data-testid={`delete-${m.id}`} onClick={()=>remove(m.id)} className="p-1.5 hover:bg-red-50 rounded-md text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: card list */}
        <div className="sm:hidden divide-y divide-slate-100">
          {markets.map(m => (
            <div key={m.id} className="p-4 space-y-2.5" data-testid={`card-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{m.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">🕒 {m.open_time} → {m.close_time}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${m.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{m.status}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-400 font-medium">Result:</span>
                <span className="font-mono font-semibold text-slate-800">{m.open_result || "***"} - {m.close_result || "***"}</span>
              </div>
              {m.reminder_message && (
                <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 text-amber-800">
                  🔔 <b>Reminder:</b> {m.reminder_message.slice(0, 60)}{m.reminder_message.length > 60 && '…'}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Dialog open={open && editing?.id === m.id} onOpenChange={(o)=>{ setOpen(o); if(!o) setEditing(null); }}>
                  <DialogTrigger asChild>
                    <button data-testid={`edit-card-${m.id}`} onClick={()=>{setEditing({...m}); setOpen(true);}} className="flex-1 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold flex items-center justify-center gap-1.5">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  </DialogTrigger>
                  <MarketDialog editing={editing} setEditing={setEditing} save={save} />
                </Dialog>
                <button data-testid={`delete-card-${m.id}`} onClick={()=>remove(m.id)} className="px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-sm font-semibold">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          {markets.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No markets yet</div>}
        </div>
      </div>
    </AdminLayout>
  );
}

function MarketDialog({ editing, setEditing, save }) {
  if (!editing) return null;
  return (
    <DialogContent className="bg-white">
      <DialogHeader><DialogTitle>{editing.id ? "Edit Market" : "Add Market"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Name</Label><Input data-testid="market-name-input" value={editing.name} onChange={(e)=>setEditing({...editing, name: e.target.value})} className="mt-1.5" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Open Time</Label><Input data-testid="market-open-input" type="time" value={editing.open_time} onChange={(e)=>setEditing({...editing, open_time: e.target.value})} className="mt-1.5" /></div>
          <div><Label>Close Time</Label><Input data-testid="market-close-input" type="time" value={editing.close_time} onChange={(e)=>setEditing({...editing, close_time: e.target.value})} className="mt-1.5" /></div>
        </div>
        <div className="flex items-center justify-between">
          <Label>Active</Label>
          <Switch data-testid="market-status-switch" checked={editing.status === "active"} onCheckedChange={(v)=>setEditing({...editing, status: v ? "active" : "inactive"})} />
        </div>
        <div>
          <Label className="flex items-center gap-1.5">
            <span>🔔</span>
            <span>Reminder Message</span>
            <span className="text-[10px] text-slate-400 font-normal">(15 min before close)</span>
          </Label>
          <textarea
            data-testid="market-reminder-input"
            value={editing.reminder_message || ""}
            onChange={(e)=>setEditing({...editing, reminder_message: e.target.value})}
            placeholder="Example: Jaldi lagayein! Sirf 15 min bache hain — DESAWAR band hone wala hai 🎯"
            rows={3}
            className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <div className="text-[11px] text-slate-500 mt-1">Blank chodo agar reminder nahi bhejni</div>
        </div>
        <Button data-testid="market-save-btn" onClick={()=>save(editing)} className="w-full btn-brand h-10">Save</Button>
      </div>
    </DialogContent>
  );
}
